import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "./firebaseConfig";

export const fetchLastSet = async (userId, profileId, exerciseId) => {
  const logsRef = collection(db, "logs");

  const q = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", exerciseId),
    orderBy("date", "desc"),
    limit(1)
  );

  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    return querySnapshot.docs[0].data();
  }
  return null;
};

// Reconstruct the sets performed during the most recent workout session that
// actually logged this exercise before the current mission. Sessions are bounded
// by "SESSION_START" marker docs, not by a time-gap guess — rapid back-to-back
// test sessions used to get merged when clustered by a fixed 4-hour gap.
//
// Anchors directly on the exercise's own last-logged set (a single orderBy(date
// desc) limit(1) query) rather than walking a bounded list of recent session
// markers — an earlier version walked the last 10 SESSION_START markers looking
// for one that contained this exercise, which silently returned "no history" for
// any exercise/routine not touched within the last 10 mission-starts of ANY
// routine (e.g. a routine trained less often than others). Anchoring on the
// exercise itself finds the right session regardless of how many unrelated
// missions happened in between.
// missionStartTime is a JS Date (from activeSession.startTime).
export const fetchPreviousSessionSets = async (userId, profileId, exerciseId, missionStartTime) => {
  const logsRef = collection(db, "logs");

  // 1. Find this exercise's most recent logged set before the current mission.
  const lastSetQuery = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", exerciseId),
    where("date", "<", missionStartTime),
    orderBy("date", "desc"),
    limit(1)
  );
  const lastSetSnapshot = await getDocs(lastSetQuery);
  if (lastSetSnapshot.empty) return [];
  const lastSetDate = lastSetSnapshot.docs[0].data().date;
  if (!lastSetDate) return [];

  // 2. Find the SESSION_START marker that opened that session (the window's
  // lower bound) — the newest marker at or before the last logged set's date.
  const lowerMarkerQuery = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", "SESSION_START"),
    where("date", "<=", lastSetDate),
    orderBy("date", "desc"),
    limit(1)
  );
  const lowerMarkerSnapshot = await getDocs(lowerMarkerQuery);
  // No marker at/before the set is unexpected (every mission opens with one),
  // but fall back to the set's own date as the window's lower bound rather than
  // returning nothing.
  const lowerBound = lowerMarkerSnapshot.empty
    ? lastSetDate
    : lowerMarkerSnapshot.docs[0].data().date || lastSetDate;

  // 3. Find the next SESSION_START marker after that session (the window's
  // upper bound), or fall back to missionStartTime if that was the latest one.
  const upperMarkerQuery = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", "SESSION_START"),
    where("date", ">", lastSetDate),
    orderBy("date", "asc"),
    limit(1)
  );
  const upperMarkerSnapshot = await getDocs(upperMarkerQuery);
  const upperBound = upperMarkerSnapshot.empty
    ? missionStartTime
    : upperMarkerSnapshot.docs[0].data().date || missionStartTime;

  // 4. Pull every set for this exercise within that session's window.
  const setsQuery = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", exerciseId),
    where("date", ">=", lowerBound),
    where("date", "<", upperBound),
    orderBy("date", "asc")
  );
  const setsSnapshot = await getDocs(setsQuery);

  // durationMinutes/distance are included alongside weight/reps so this one
  // function serves both log shapes -- a cardio log naturally has no
  // weight/reps (and vice versa), so spreading both pairs through is
  // correct regardless of which kind of exercise this session's sets are.
  return setsSnapshot.docs.map((d) => ({
    weight: d.data().weight,
    reps: d.data().reps,
    durationMinutes: d.data().durationMinutes,
    distance: d.data().distance
  }));
};

// excludeLogId: when recomputing a PR after editing a set's weight, the log
// being edited must not count toward its own "max to beat" — pulling a few
// extra top-weight docs (instead of just 1) and filtering the excluded id out
// client-side handles this without needing a Firestore inequality filter on
// document id (which can't combine with orderBy("weight") in the same query).
export const getExerciseMax = async (userId, profileId, exerciseId, excludeLogId = null) => {
  const logsRef = collection(db, "logs");

  const q = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", exerciseId),
    orderBy("weight", "desc"),
    limit(excludeLogId ? 5 : 1)
  );

  const querySnapshot = await getDocs(q);
  const docs = excludeLogId
    ? querySnapshot.docs.filter((d) => d.id !== excludeLogId)
    : querySnapshot.docs;

  if (docs.length > 0) {
    return docs[0].data().weight;
  }
  return 0;
};

// All past logged sets for one exercise, newest-first, capped at
// maxResults. Backs the "i" history popup available wherever an exercise is
// shown. Single-equality-plus-orderBy shape, already covered by the
// existing ownerUserId+profileId+exerciseId+date composite index (same one
// fetchLastSet/fetchPreviousSessionSets/getExerciseMax already use) -- no
// new index needed.
export const fetchExerciseHistory = async (userId, profileId, exerciseId, maxResults = 50) => {
  const logsRef = collection(db, "logs");

  const q = query(
    logsRef,
    where("ownerUserId", "==", userId),
    where("profileId", "==", profileId),
    where("exerciseId", "==", exerciseId),
    orderBy("date", "desc"),
    limit(maxResults)
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const fetchUserRoutines = async (userId) => {
  const routinesRef = collection(db, "routines");
  
  const q = query(
    routinesRef,
    where("ownerUserId", "==", userId)
  );

  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  return [];
};